import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import * as Switch from '@radix-ui/react-switch';
import { Check, ChevronDown, AlertTriangle, Loader2, X, Rocket, Lightbulb, Folder, Target, Edit3, BrainCircuit, Zap, Palette, BarChart3, Flame } from 'lucide-react';
import { workspacesApi } from '../api/workspaces';
import { settingsApi, type DefaultPermission } from '../api/settings';
import { toast } from '../components/Toast';

type TabId = 'general' | 'settings' | 'danger';

const TABS: { id: TabId; label: string }[] = [
  { id: 'general', label: '通用' },
  { id: 'settings', label: '设置' },
  { id: 'danger', label: '危险区' },
];

const PERMISSION_LABEL: Record<DefaultPermission, string> = {
  view: '只读',
  comment: '可评论',
  edit: '可编辑',
};

export function SettingsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: ws, isLoading: wsLoading } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: () => workspacesApi.get(wsId),
    enabled: !!wsId,
  });
  const { data: settings } = useQuery({
    queryKey: ['workspace-settings', wsId],
    queryFn: () => settingsApi.get(wsId),
    enabled: !!wsId,
  });

  const isAdmin = ws?.currentRole === 'admin';

  const [tab, setTab] = useState<TabId>('general');

  // 通用表单
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  useEffect(() => {
    if (ws) {
      setName(ws.name);
      setEmoji(ws.emoji ?? '');
    }
  }, [ws]);

  // 设置表单
  const [defaultPermission, setDefaultPermission] = useState<DefaultPermission>('view');
  const [allowPublicSharing, setAllowPublicSharing] = useState(true);
  useEffect(() => {
    if (settings) {
      setDefaultPermission(settings.defaultPermission);
      setAllowPublicSharing(settings.allowPublicSharing);
    }
  }, [settings]);

  // 危险区
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const updateWorkspaceMutation = useMutation({
    mutationFn: () =>
      workspacesApi.update(wsId, {
        name: name.trim() || '未命名空间',
        emoji: emoji || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', wsId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('空间信息已更新');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '保存失败');
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: () =>
      settingsApi.update(wsId, { defaultPermission, allowPublicSharing }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-settings', wsId] });
      toast.success('设置已更新');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '保存失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => workspacesApi.remove(wsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.removeQueries({ queryKey: ['workspace', wsId] });
      toast.success('空间已删除');
      navigate('/spaces');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '删除失败');
    },
  });

  if (wsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      <h1 className="text-2xl font-semibold">空间设置</h1>

      {/* Tab 切换 */}
      <div className="mt-6 flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-accent text-accent'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
            aria-current={tab === t.id}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 通用 */}
      {tab === 'general' && (
        <div className="mt-6 card p-6">
          <h2 className="text-base font-medium">通用</h2>
          <div className="mt-4 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="ws-name-input">
                空间名称
              </label>
              <input
                id="ws-name-input"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={32}
                disabled={!isAdmin}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="ws-icon-input">
                空间图标
              </label>
              <div className="flex flex-wrap gap-2">
                {['Rocket', 'Lightbulb', 'Folder', 'Target', 'Edit3', 'BrainCircuit', 'Zap', 'Palette', 'BarChart3', 'Flame'].map((iconName) => {
                  const IconComp = iconName === 'Rocket' ? Rocket :
                    iconName === 'Lightbulb' ? Lightbulb :
                    iconName === 'Folder' ? Folder :
                    iconName === 'Target' ? Target :
                    iconName === 'Edit3' ? Edit3 :
                    iconName === 'BrainCircuit' ? BrainCircuit :
                    iconName === 'Zap' ? Zap :
                    iconName === 'Palette' ? Palette :
                    iconName === 'BarChart3' ? BarChart3 :
                    iconName === 'Flame' ? Flame : Folder;
                  const isSelected = emoji === iconName;
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setEmoji(iconName)}
                      className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-accent bg-accent-50 text-accent'
                          : 'border-border text-text-secondary hover:bg-bg-subtle hover:text-text-primary'
                      }`}
                      disabled={!isAdmin}
                      aria-pressed={isSelected}
                      aria-label={`选择图标 ${iconName}`}
                    >
                      <IconComp className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-text-tertiary">选择一个图标</p>
            </div>
            {isAdmin && (
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  disabled={updateWorkspaceMutation.isPending}
                  onClick={() => updateWorkspaceMutation.mutate()}
                >
                  {updateWorkspaceMutation.isPending ? '保存中…' : '保存'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 设置 */}
      {tab === 'settings' && (
        <div className="mt-6 card p-6">
          <h2 className="text-base font-medium">权限与分享</h2>
          <div className="mt-4 space-y-5">
            <div>
              <label className="mb-1 block text-sm font-medium">默认权限</label>
              <p className="mb-2 text-xs text-text-tertiary">新加入成员的默认操作权限</p>
              <Select.Root
                value={defaultPermission}
                onValueChange={(v) => setDefaultPermission(v as DefaultPermission)}
                disabled={!isAdmin}
              >
                <Select.Trigger className="input flex w-[200px] items-center justify-between">
                  <Select.Value>{PERMISSION_LABEL[defaultPermission]}</Select.Value>
                  <Select.Icon>
                    <ChevronDown className="h-3.5 w-3.5 text-text-tertiary" />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content
                    className="z-50 min-w-[200px] overflow-hidden rounded-md border border-border bg-bg-surface shadow-md"
                    position="popper"
                    sideOffset={4}
                  >
                    <Select.Viewport className="p-1">
                      {(['view', 'comment', 'edit'] as DefaultPermission[]).map((p) => (
                        <Select.Item
                          key={p}
                          value={p}
                          className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-bg-subtle"
                        >
                          <Select.ItemText>{PERMISSION_LABEL[p]}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Check className="h-3.5 w-3.5 text-accent" />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">允许公开分享</div>
                <p className="text-xs text-text-tertiary">允许成员生成对外的分享链接</p>
              </div>
              <Switch.Root
                checked={allowPublicSharing}
                onCheckedChange={setAllowPublicSharing}
                disabled={!isAdmin}
                className="relative h-6 w-11 rounded-full bg-bg-subtle data-[state=checked]:bg-accent outline-none disabled:opacity-50"
              >
                <Switch.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[22px]" />
              </Switch.Root>
            </div>

            {isAdmin && (
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  disabled={updateSettingsMutation.isPending}
                  onClick={() => updateSettingsMutation.mutate()}
                >
                  {updateSettingsMutation.isPending ? '保存中…' : '保存'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 危险区 */}
      {tab === 'danger' && (
        <div className="mt-6 rounded-lg border border-danger/30 bg-[#fef2f2] p-6">
          <h2 className="flex items-center gap-2 text-base font-medium text-danger">
            <AlertTriangle className="h-4 w-4" /> 危险区
          </h2>
          <p className="mt-2 text-sm text-text-secondary">
            删除空间将移除所有文档、思维导图与成员关系（30 天回收站可恢复）。该操作不可逆，请谨慎。
          </p>
          {isAdmin ? (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="btn btn-danger btn-md"
                onClick={() => setDeleteOpen(true)}
              >
                删除空间
              </button>
            </div>
          ) : (
            <p className="mt-4 text-xs text-text-tertiary">仅管理员可删除空间</p>
          )}
        </div>
      )}

      {/* 删除确认弹窗 */}
      <Dialog.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-bg-surface p-6 shadow-xl"
            aria-describedby={undefined}
          >
            <div className="flex items-start justify-between">
              <Dialog.Title className="text-lg font-semibold text-danger">删除空间</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle"
                  aria-label="关闭"
                >
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="mt-2 text-sm text-text-secondary">
              此操作将移除空间内的所有内容。请输入空间名{' '}
              <span className="font-medium text-text-primary">{ws?.name}</span>{' '}
              以确认删除。
            </Dialog.Description>
            <div className="mt-4">
              <input
                className="input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={ws?.name ?? ''}
                autoFocus
              />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeleteConfirm('');
                }}
                className="btn btn-ghost btn-md"
              >
                取消
              </button>
              <button
                type="button"
                disabled={deleteConfirm !== ws?.name || deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
                className="btn btn-danger btn-md"
              >
                {deleteMutation.isPending ? '删除中…' : '永久删除'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
